import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import { useCmsEmbedded } from '~/components/AiChat/hooks/useCmsEmbedded';
import { useClaim } from '~/components/ClaimContainer';
import Button from '~/components/core/Atomic/Buttons/Button';
import Text from '~/components/core/TextComponents/Text';
import useCurrencyFormatter from '~/components/CurrencyFormatterContext';
import LoadingIndicator from '~/components/LoadingIndicator';
import { stringCmp } from '~/Utils';
import { getDeductibleText } from '~/Utils/deductibleUtils';

import SortableTable from '../../../core/Tables/SortableTable';

interface CoverageBreakdownData {
  id: number;
  coverage: string;
  submittedAmount: string;
  notCovered: string;
  covered: string;
  coInsurance: string;
  eligibleForReimbursement: string;
  accumulatedDeductible: string;
  remainingDeductible: string;
  deductibleToApply: string;
  remainingLimit: string;
  excess: string;
  deductedToDate: string;
  paidToDate: string;
  remainingToPay: string;
  actions: React.ReactNode;
}

interface DetailedCoverageTotalsResponse {
  coverage_id: number;
  coverage_key: string;
  coverage_name: string;
  submitted_amount: number;
  covered_amount: number;
  not_covered_amount: number;
  co_insurance: number;
  eligible_for_reimbursement: number;
  accumulated_deductible: number;
  remaining_deductible: number;
  remaining_limit: number | null;
  deductible_to_apply: number;
  excess: number | null;
  paid_to_date: number;
  deducted_to_date: number;
  remaining_to_pay: number;
}

interface ApiResponse {
  breakdown: {
    coverages: DetailedCoverageTotalsResponse[];
    total_excluded_amount: number;
    total_submitted: number;
    total_covered: number;
    total_not_covered_and_excluded: number;
    total_excess: number;
    total_remaining_to_pay: number;
  };
  metadata: {
    with_coinsurance: boolean;
  };
}

const CoverageBreakdownTab: React.FC = () => {
  const [apiData, setApiData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [isPaymentInProgress, setIsPaymentInProgress] = useState<boolean>(false);
  const { currencyFormatter } = useCurrencyFormatter();
  const { claim } = useClaim({ claimRequired: true });
  const { applyCoveragePayment } = useCmsEmbedded();

  const fetchCoverageBreakdowns = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await axios.get<ApiResponse>(
        `/api/v1/claims/${claim.id}/invoice_based_da/claim_invoice_coverage_breakdown`
      );
      setApiData(response.data);
      setLoading(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching coverage breakdowns:', error);
      setError(true);
      setLoading(false);
    }
  }, [claim.id]);

  // Data fetching in useEffect
  useEffect(() => {
    if (claim) {
      fetchCoverageBreakdowns();
    }
  }, [claim, fetchCoverageBreakdowns]);

  // Data transformation using useMemo
  const coverageData = useMemo(() => {
    if (!apiData) return [];

    // Transform API data to match our table structure
    const transformedData: CoverageBreakdownData[] = apiData.breakdown.coverages.map((item, index) => {
      const isPaymentDisabled = isPaymentInProgress || item.remaining_to_pay <= 0;
      return {
        id: index + 1,
        coverage: item.coverage_name,
        submittedAmount: currencyFormatter.format(item.submitted_amount),
        notCovered: currencyFormatter.format(item.not_covered_amount),
        covered: currencyFormatter.format(item.covered_amount),
        coInsurance: currencyFormatter.format(item.co_insurance),
        eligibleForReimbursement: currencyFormatter.format(item.eligible_for_reimbursement),
        remainingLimit: item.remaining_limit !== null ? currencyFormatter.format(item.remaining_limit) : '',
        accumulatedDeductible: currencyFormatter.format(item.accumulated_deductible),
        remainingDeductible: currencyFormatter.format(item.remaining_deductible),
        deductibleToApply: currencyFormatter.format(item.deductible_to_apply),
        excess: item.excess !== null ? currencyFormatter.format(item.excess) : '',
        deductedToDate: currencyFormatter.format(item.deducted_to_date),
        paidToDate: currencyFormatter.format(item.paid_to_date),
        remainingToPay: currencyFormatter.format(item.remaining_to_pay),
        actions:
          item.remaining_to_pay !== null ? (
            <Button
              color="primary"
              disabled={isPaymentDisabled}
              onClick={async () => {
                setIsPaymentInProgress(true);
                try {
                  await applyCoveragePayment({
                    coverageId: item.coverage_id,
                    onPaymentDone: () => {
                      fetchCoverageBreakdowns();
                    },
                  });
                } finally {
                  setIsPaymentInProgress(false);
                }
              }}
            >
              Pay
            </Button>
          ) : null,
      };
    });

    // Add excluded row
    transformedData.push({
      id: transformedData.length + 1,
      coverage: 'Excluded',
      submittedAmount: currencyFormatter.format(apiData.breakdown.total_excluded_amount),
      notCovered: '',
      covered: '',
      coInsurance: '',
      eligibleForReimbursement: '',
      remainingLimit: '',
      accumulatedDeductible: '',
      remainingDeductible: '',
      deductibleToApply: '',
      excess: '',
      deductedToDate: '',
      paidToDate: '',
      remainingToPay: '',
      actions: null,
    });

    return transformedData;
  }, [apiData, currencyFormatter, applyCoveragePayment, fetchCoverageBreakdowns, isPaymentInProgress]);

  // Define columns for the main table
  const columns = [
    {
      id: 'coverage',
      label: 'Coverage',
      disableSort: true,
      specialCmpFunc: (a: CoverageBreakdownData, b: CoverageBreakdownData) =>
        a.coverage === 'Excluded' ? 1 : stringCmp(a.coverage, b.coverage),
    },
    { id: 'submittedAmount', label: 'Submitted' },
    { id: 'notCovered', label: 'Not Covered' },
    { id: 'covered', label: 'Covered' },
    { id: 'coInsurance', label: 'Co-Insurance', isHidden: !apiData?.metadata.with_coinsurance },
    {
      id: 'eligibleForReimbursement',
      label: 'Eligible for Reimbursement',
      isHidden: !apiData?.metadata.with_coinsurance,
    },
    { id: 'remainingDeductible', label: `Remaining ${getDeductibleText()}` },
    { id: 'deductibleToApply', label: `${getDeductibleText()} to Apply` },
    { id: 'deductedToDate', label: 'Deducted to Date' },
    { id: 'paidToDate', label: 'Paid to Date' },
    { id: 'remainingLimit', label: 'Remaining Limit' },
    { id: 'excess', label: 'Excess' },
    { id: 'remainingToPay', label: 'Remaining to Pay' },
    { id: 'actions', label: '', disableSort: true },
  ];

  if (loading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <LoadingIndicator isError />;
  }

  const summaryItems = [
    { label: 'Submitted', value: apiData?.breakdown.total_submitted ?? 0 },
    { label: 'Covered', value: apiData?.breakdown.total_covered ?? 0 },
    { label: 'Excluded & Not Covered', value: apiData?.breakdown.total_not_covered_and_excluded ?? 0 },
    { label: 'Excess', value: apiData?.breakdown.total_excess ?? 0 },
    { label: 'Remaining to Pay', value: apiData?.breakdown.total_remaining_to_pay ?? 0 },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div className="mb-16 flex flex-row items-center justify-start gap-32 rounded-md bg-blueSky-100 px-24 py-16">
        {summaryItems.map((item) => (
          <div key={item.label} className="flex flex-row items-baseline gap-8">
            <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.MEDIUM} className="text-slate-800">
              {item.label}:
            </Text>
            <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="text-slate-900">
              {currencyFormatter.format(item.value)}
            </Text>
          </div>
        ))}
      </div>
      <SortableTable columns={columns} rows={coverageData} defaultOrderColumn={0} order="asc" />
    </div>
  );
};

export default CoverageBreakdownTab;
